import { useAuth } from "@/lib/auth";
import { isDesktopShell } from "@/lib/desktop-shell";
import { shouldTryWannlabsSso } from "@/lib/wannlabs-sso";
import { startWannlabsSso } from "@/lib/wannlabs-pkce";
import { useEffect, useState } from "react";
import { useHashScroll } from "@/lib/use-hash-scroll";
import { useRevealOnScroll } from "@/hooks/use-reveal-on-scroll";
import { GlassCard } from "@/components/glass-card";
import { LandingHeroVisual } from "@/components/landing-hero-visual";
import { LandingLifestyleSection } from "@/components/landing-lifestyle-section";
import { LandingScreensSection } from "@/components/landing-screens-section";
import { MarketingShell } from "@/components/marketing-shell";
import { PricingPlans } from "@/components/pricing-plans";
import { SignInButton } from "@/components/sign-in-button";
import {
  BYO_CLAUDE_MONTHLY_PRICE_CAD,
  FLAT_MONTHLY_PRICE_CAD,
} from "@/lib/pricing-copy";
import { POST_LOGIN_PATH } from "@prime-assistant/shared";
import { Link, Navigate } from "react-router-dom";

const steps = [
  {
    title: "Sign in",
    body: "Email and password, a passkey, or Google — then you are in.",
    accent: "from-teal-500/20 to-emerald-400/10",
  },
  {
    title: "Connect your mortgage account",
    body: "One tap to link Velocity. We handle the rest.",
    accent: "from-cyan-500/15 to-teal-500/10",
  },
  {
    title: "Start chatting",
    body: "Ask for follow-ups, bookings, and application checks.",
    accent: "from-emerald-500/15 to-slate-400/10",
  },
];

function HowItWorks() {
  const ref = useRevealOnScroll<HTMLElement>();

  return (
    <section
      id="how-it-works"
      ref={ref}
      className="landing-reveal mt-16 scroll-mt-24"
      aria-labelledby="how-it-works-heading"
    >
      <header className="mb-6 max-w-2xl">
        <p className="text-sm font-medium text-teal-800">How it works</p>
        <h2
          id="how-it-works-heading"
          className="mt-2 font-[family-name:var(--font-display)] text-3xl font-semibold tracking-tight text-slate-900"
        >
          Ready in three taps
        </h2>
      </header>
      <ol className="grid gap-4 md:grid-cols-3">
        {steps.map((step, i) => (
          <li key={step.title}>
            <GlassCard
              className={`landing-card-lift h-full bg-gradient-to-br ${step.accent}`}
            >
              <p
                className="flex h-9 w-9 items-center justify-center rounded-2xl bg-teal-700 text-sm font-bold text-white shadow-md shadow-teal-700/25"
                aria-hidden
              >
                {i + 1}
              </p>
              <h3 className="mt-4 text-lg font-semibold text-slate-900">
                {step.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                {step.body}
              </p>
            </GlassCard>
          </li>
        ))}
      </ol>
    </section>
  );
}

function AiChoiceSection() {
  const ref = useRevealOnScroll<HTMLElement>();

  return (
    <section
      ref={ref}
      className="landing-reveal mt-16"
      aria-labelledby="ai-choice-heading"
    >
      <GlassCard className="overflow-hidden bg-gradient-to-br from-teal-50/80 via-white/60 to-emerald-50/50">
        <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
          <div>
            <h2
              id="ai-choice-heading"
              className="font-[family-name:var(--font-display)] text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl"
            >
              Hosted AI or your own Claude
            </h2>
            <p className="mt-3 max-w-3xl text-slate-600">
              Prefer Claude? Choose the{" "}
              <strong className="font-semibold text-slate-800">
                My Claude
              </strong>{" "}
              plan (${BYO_CLAUDE_MONTHLY_PRICE_CAD}/mo platform fee), paste your
              Anthropic API key in Settings, and chat and helpers run Claude
              Agents-style tool use against your connected Velocity, Gmail,
              Drive, Twilio, and web tools — and, when your Prime connector
              URL is public HTTPS, against that connector catalog too. Model usage
              bills to Anthropic; the ${BYO_CLAUDE_MONTHLY_PRICE_CAD} covers
              Prime&apos;s workspace and hosting.
            </p>
            <ul className="mt-5 grid gap-3 text-sm text-slate-700 md:grid-cols-3">
              <li className="landing-card-lift rounded-2xl border border-teal-600/10 bg-white/70 px-4 py-3 shadow-sm">
                <p className="font-semibold text-teal-900">Chat &amp; automated assistants</p>
                <p className="mt-1 text-slate-600">
                  Same Prime workflows — drafts, follow-ups, pipeline asks —
                  powered by Claude when you switch.
                </p>
              </li>
              <li className="landing-card-lift rounded-2xl border border-teal-600/10 bg-white/70 px-4 py-3 shadow-sm">
                <p className="font-semibold text-teal-900">Your connections</p>
                <p className="mt-1 text-slate-600">
                  Tools stay on accounts you connect in Settings. Claude proposes
                  steps; Prime executes them securely.
                </p>
              </li>
              <li className="landing-card-lift rounded-2xl border border-teal-600/10 bg-white/70 px-4 py-3 shadow-sm">
                <p className="font-semibold text-teal-900">Connector catalog</p>
                <p className="mt-1 text-slate-600">
                  Optional Connectors page for Claude Desktop / Cursor, and My
                  Claude can attach that same public connector catalog in-app.
                </p>
              </li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/pricing"
                className="rounded-2xl bg-teal-700 px-5 py-3 text-sm font-semibold text-white shadow-md shadow-teal-700/20 transition hover:bg-teal-800 hover:shadow-lg hover:shadow-teal-700/25"
              >
                See pricing
              </Link>
              <Link
                to="/signup"
                className="rounded-2xl border border-white/60 bg-white/85 px-5 py-3 text-sm font-semibold text-slate-900 backdrop-blur transition hover:bg-white"
              >
                Create account
              </Link>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-3xl shadow-[0_30px_70px_-36px_rgba(15,23,42,0.5)] ring-1 ring-white/80">
            <img
              src="/landing/prime-advisor-tablet.jpg"
              alt="Advisor and customer collaborating with Mortgage Broker Assistant on a tablet"
              className="aspect-[4/5] w-full object-cover sm:aspect-[5/4] lg:aspect-[4/5]"
              loading="lazy"
              decoding="async"
            />
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-teal-900/25 via-transparent to-emerald-400/10"
              aria-hidden
            />
          </div>
        </div>
      </GlassCard>
    </section>
  );
}

export default function LandingPage() {
  const { user, status, googleConfigured, demoAllowed } = useAuth();
  const signedIn = status === "authenticated" && Boolean(user);
  const pricingRef = useRevealOnScroll<HTMLElement>();
  useHashScroll();

  const [ssoRedirecting, setSsoRedirecting] = useState(false);
  useEffect(() => {
    if (isDesktopShell() || status !== "unauthenticated") return;
    if (shouldTryWannlabsSso(window.location, document.referrer)) {
      setSsoRedirecting(true);
      void startWannlabsSso("").catch(() => setSsoRedirecting(false));
    }
  }, [status]);

  // Signed-in users (including those arriving from other Wannlabs apps) go straight
  // into the app. A section hash (#pricing etc.) still shows the marketing page.
  if (!isDesktopShell() && signedIn && !window.location.hash) {
    return <Navigate to={POST_LOGIN_PATH} replace />;
  }
  if (!isDesktopShell() && (ssoRedirecting || (status === "loading" && new URLSearchParams(window.location.search).get("wl") === "1"))) {
    return null;
  }

  if (isDesktopShell()) {
    if (status === "loading") {
      return null;
    }
    if (signedIn) {
      return <Navigate to={POST_LOGIN_PATH} replace />;
    }
    return <Navigate to="/login" replace />;
  }

  return (
    <MarketingShell
      googleConfigured={googleConfigured}
      demoAllowed={demoAllowed}
    >
      <section
        id="product"
        className="relative scroll-mt-24 overflow-hidden rounded-[2rem] border border-white/60 bg-gradient-to-br from-teal-50/90 via-white/40 to-slate-100/70 p-6 shadow-[0_40px_100px_-48px_rgba(15,118,110,0.35)] backdrop-blur-sm md:p-10"
        aria-labelledby="product-heading"
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          aria-hidden
        >
          <div className="landing-mesh absolute inset-0" />
        </div>

        <div className="relative grid items-center gap-12 lg:grid-cols-2 lg:gap-10">
          <div className="landing-fade-in">
            <p className="font-[family-name:var(--font-display)] text-sm font-semibold tracking-[0.08em] text-teal-800 uppercase">
              Mortgage Broker Assistant
            </p>
            <h1
              id="product-heading"
              className="mt-3 font-[family-name:var(--font-display)] text-4xl font-semibold tracking-tight text-slate-900 md:text-5xl lg:text-[3.25rem] lg:leading-[1.1]"
            >
              Your mortgage assistant,{" "}
              <span className="bg-gradient-to-r from-teal-700 to-emerald-600 bg-clip-text text-transparent">
                ready in three taps.
              </span>
            </h1>
            <p className="mt-4 max-w-xl text-lg leading-relaxed text-slate-600">
              Keep clients warm, your calendar full, and applications tidy — so
              you can focus on people. Chat and helpers run on hosted AI, or plug
              in your own Claude key.
            </p>
            <p className="mt-3 max-w-xl text-sm text-slate-600">
              After a small free try, choose{" "}
              <strong className="font-semibold text-slate-800">
                Pro flat (${FLAT_MONTHLY_PRICE_CAD}/mo)
              </strong>
              ,{" "}
              <strong className="font-semibold text-slate-800">Pro per-use</strong>
              , or{" "}
              <strong className="font-semibold text-slate-800">
                My Claude (${BYO_CLAUDE_MONTHLY_PRICE_CAD}/mo)
              </strong>{" "}
              with your own Anthropic key. Hosted Pro covers Google Cloud, Modal,
              and our servers; My Claude is a separate platform subscription.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {signedIn ? (
                <>
                  <Link
                    to={POST_LOGIN_PATH}
                    className="rounded-2xl bg-teal-700 px-5 py-3 text-sm font-semibold text-white shadow-md shadow-teal-700/25 transition hover:-translate-y-0.5 hover:bg-teal-800 hover:shadow-lg"
                  >
                    Open app
                  </Link>
                  <Link
                    to="/settings"
                    className="rounded-2xl border border-white/70 bg-white/85 px-5 py-3 text-sm font-semibold text-slate-900 backdrop-blur transition hover:bg-white/80"
                  >
                    Settings
                  </Link>
                </>
              ) : (
                <>
                  <SignInButton
                    googleConfigured={googleConfigured}
                    demoAllowed={demoAllowed}
                    label="Sign up with Google"
                    redirectTo="/onboarding"
                  />
                  <a
                    href="#pricing"
                    className="rounded-2xl border border-white/70 bg-white/85 px-5 py-3 text-sm font-semibold text-slate-900 backdrop-blur transition hover:bg-white/80"
                  >
                    See pricing
                  </a>
                  <Link
                    to="/login"
                    className="rounded-2xl border border-white/70 bg-white/85 px-5 py-3 text-sm font-semibold text-slate-900 backdrop-blur transition hover:bg-white/80"
                  >
                    Sign in
                  </Link>
                </>
              )}
            </div>
            {!googleConfigured && demoAllowed ? (
              <p className="mt-4 max-w-md text-xs text-slate-500">
                Dev mode: Google OAuth is not configured. Sign-in uses a demo
                broker session only.
              </p>
            ) : null}
          </div>

          <LandingHeroVisual />
        </div>
      </section>

      <HowItWorks />

      <LandingLifestyleSection />

      <LandingScreensSection />

      <section
        id="pricing"
        ref={pricingRef}
        className="landing-reveal mt-16 scroll-mt-24"
        aria-labelledby="pricing-heading"
      >
        <header className="mb-6 max-w-2xl">
          <p className="text-sm font-medium text-teal-800">Pricing</p>
          <h2
            id="pricing-heading"
            className="mt-2 font-[family-name:var(--font-display)] text-3xl font-semibold tracking-tight text-slate-900"
          >
            Hosted Pro or My Claude — pick your plan
          </h2>
          <p className="mt-3 text-slate-600">
            Flat or per-use Pro for Emma, or My Claude at $
            {BYO_CLAUDE_MONTHLY_PRICE_CAD}/mo if you bring your Anthropic key.
            Chat and helpers need an active subscription either way.
          </p>
        </header>
        <PricingPlans />
        <p className="mt-4 text-center text-sm text-slate-500">
          List rates in CAD.{" "}
          <Link
            to="/pricing"
            className="font-medium text-teal-800 underline"
          >
            Full pricing page
          </Link>
        </p>
      </section>

      <AiChoiceSection />
    </MarketingShell>
  );
}
